import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Lottie from 'lottie-react';
import { fetchProductById, fetchProductBySlug } from '../services/productsService';
import { useCart } from '../contexts/CartContext';
import { useKiosk } from '../contexts/KioskContext';
import { useLocale } from '../hooks/useLocale';
import { formatPrice as fmtPrice } from '../utils/format';
import { tField } from '../utils/i18nContent';
import { productDetail as proxyDetail, artistAvatar as proxyAvatar } from '../lib/imageProxy';
import BlurImage from '../components/BlurImage';
import loadingAnimation from '../assets/loading.json';

const ProductPage = () => {
  // Polymorphic param — accepts either a numeric ID (legacy URLs) or a slug.
  // Naming matches /artist/:identifier for consistency.
  const { identifier } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isKiosk } = useKiosk();
  const locale = useLocale();
  const { t } = useTranslation('product');
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [artist, setArtist] = useState(null);
  const [artistLoading, setArtistLoading] = useState(false);
  const [artistError, setArtistError] = useState(null);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        // If the identifier contains any non-digit char it can only be a slug.
        // If it's all digits it's almost certainly a legacy ID — but we fall
        // back to a slug lookup in case a numeric slug is ever introduced, so
        // the URL never silently resolves to the wrong product.
        const isNumeric = /^\d+$/.test(identifier);
        let data = isNumeric
          ? await fetchProductById(parseInt(identifier, 10))
          : await fetchProductBySlug(identifier);
        if (!data && isNumeric) data = await fetchProductBySlug(identifier);

        if (!data) {
          setError('notFound');
          return;
        }
        setProduct(data);
        setError(null);
      } catch (err) {
        console.error('Error loading product:', err);
        // Either a known key (translated at render time) or a raw message
        // from the server. `t(key)` returns the key literal if it's not in
        // the namespace, so raw strings render unchanged.
        setError(err.message || 'loadError');
      } finally {
        setLoading(false);
      }
    };

    if (identifier) {
      loadProduct();
    }
  }, [identifier, navigate]);

  // Canonicalize the URL: when a user arrives at the legacy /product/<id> path
  // and the product has a slug, swap to /product/<slug> so search engines and
  // shared links converge on a single canonical URL.
  useEffect(() => {
    if (product?.slug && /^\d+$/.test(identifier) && identifier !== product.slug) {
      navigate(`/product/${product.slug}`, { replace: true });
    }
  }, [product, identifier, navigate]);
  
  const colorVariants = product?.colorVariants || [];
  const hasVariants = colorVariants.length > 0;
  const selectedVariant = hasVariants
    ? (colorVariants.find((v) => v.id === selectedVariantId) || null)
    : null;

  // Check if product is sold out (from database). With color variants, the
  // shopper must first pick one — its own isSoldOut then applies; the
  // top-level flag (kept in sync with the variant aggregate server-side)
  // still short-circuits everything sold out or hidden by an admin override.
  const isSoldOut = product
    ? (product.isSoldOut || false) || (hasVariants && (!selectedVariant || selectedVariant.isSoldOut))
    : false;

  const formatPrice = (price) => fmtPrice(price, 'MXN', locale);

  const handleAddToCart = () => {
    if (!isSoldOut && product) {
      addToCart(product, selectedVariant);
    }
  };

  const handleSelectVariant = (variantId) => {
    setSelectedVariantId(variantId);
    setCurrentImageIndex(0);
  };

  const displayName = product ? (tField(product, 'name', locale) || product.name || '') : '';
  const displayDescription = product ? (tField(product, 'description', locale) || product.description || '') : '';
  const displaySeries = product ? (tField(product, 'series', locale) || product.series || '') : '';
  // When the artist has priority (creatorAboutFirst), their own name leads
  // over the project/brand name — mirrors the swap on the artist's own page.
  const artistDisplayName = (artist?.creator_about_first && artist?.creator_name)
    ? artist.creator_name
    : (artist?.artist_name || artist?.name || product?.artist || '');
  // Same priority swap for the bio text: the creator's own "about" copy
  // leads when creatorAboutFirst is set, falling back to the project/brand
  // "about" otherwise (and to the project's when the creator has none).
  const artistAboutText = artist
    ? (artist.creator_about_first
      ? (tField(artist, 'creator_about', locale) || tField(artist, 'about', locale))
      : tField(artist, 'about', locale))
    : '';

  // Get all available images for the carousel. Picking a color variant swaps
  // both the front and back images for that variant's own — falling back to
  // the product's generic image/imageBack when the variant doesn't have its
  // own (imageBack is optional per variant).
  const displayImageBack = selectedVariant?.imageBack || product?.imageBack;
  const productImages = product ? [
    { src: selectedVariant?.image || product.image, alt: displayName },
    ...(displayImageBack ? [{ src: displayImageBack, alt: `${displayName} - ${t('carousel.backView')}` }] : [])
  ] : [];

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? productImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === productImages.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (index) => {
    setCurrentImageIndex(index);
  };

  // Reset image index and default color selection whenever the product
  // changes. Defaults to the first in-stock variant so the page never opens
  // with a disabled "add to cart" for a product that does have stock — falls
  // back to the first variant at all if every color is sold out.
  useEffect(() => {
    if (product) {
      setCurrentImageIndex(0);
      const variants = product.colorVariants || [];
      const defaultVariant = variants.find((v) => !v.isSoldOut) || variants[0] || null;
      setSelectedVariantId(defaultVariant?.id || null);
    }
  }, [product]);

  // Fetch artist information when product has artist_id
  useEffect(() => {
    const loadArtist = async () => {
      if (!product?.artist_id) {
        setArtist(null);
        return;
      }

      try {
        setArtistLoading(true);
        setArtistError(null);
        console.log('Loading artist with ID:', product.artist_id);
        
        const response = await fetch(`/api/artist/${product.artist_id}`);
        
        // Check content type before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('Unexpected response type. Expected JSON but got:', contentType);
          console.error('Response preview:', text.substring(0, 200));
          console.error('Response status:', response.status, response.statusText);
          console.error('Response URL:', response.url);
          
          // If we got HTML, it means the API route wasn't found (likely routing issue)
          if (contentType && contentType.includes('text/html')) {
            const errorMsg = process.env.NODE_ENV === 'development'
              ? 'API route not found. In development, make sure to run "npm run dev" to start both servers.'
              : 'API route not available. Please check the deployment configuration.';
            throw new Error(errorMsg);
          }
          throw new Error(`Server returned unexpected response type: ${contentType || 'unknown'}`);
        }
        
        if (!response.ok) {
          if (response.status === 404) {
            console.warn('Artist not found for ID:', product.artist_id);
            setArtist(null);
            return;
          }
          
          // Try to parse error response as JSON
          let errorData = {};
          try {
            const errorText = await response.text();
            errorData = errorText ? JSON.parse(errorText) : {};
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
          }
          
          const errorMessage = errorData.error 
            || errorData.message 
            || `Error ${response.status}: ${response.statusText}`;
          
          console.error('API error response:', {
            status: response.status,
            statusText: response.statusText,
            error: errorMessage,
            errorData
          });
          
          throw new Error(errorMessage);
        }
        
        const artistData = await response.json();
        console.log('Artist data received:', artistData);
        
        if (!artistData || (!artistData.artist_name && !artistData.name)) {
          console.warn('Artist data received but seems invalid:', artistData);
          setArtist(null);
          return;
        }
        
        setArtist(artistData);
        setArtistError(null);
      } catch (err) {
        console.error('Error loading artist:', {
          message: err.message,
          stack: err.stack,
          artistId: product?.artist_id
        });
        setArtistError(err.message || 'artistSection.loadError');
        setArtist(null);
      } finally {
        setArtistLoading(false);
      }
    };

    if (product?.artist_id) {
      loadArtist();
    }
  }, [product?.artist_id]);

  if (loading) {
    return (
      <div className="container" style={{ padding: '4rem 0', textAlign: 'center' }}>
        <Lottie
          animationData={loadingAnimation}
          loop
          style={{ width: 200, height: 200, margin: '0 auto' }}
        />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container" style={{ padding: '4rem 0', textAlign: 'center' }}>
        <p style={{ color: '#dc3545', marginBottom: '1rem', fontSize: '1.2rem' }}>
          {error ? t(error) : t('notFound')}
        </p>
        {process.env.NODE_ENV === 'development' && error && (
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
            ID buscado: {identifier}
          </p>
        )}
        <button onClick={() => navigate('/')} className="btn btn-primary" style={{ marginTop: '1rem' }}>
          {t('backHome')}
        </button>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '2rem 0' }}>

      <div className="product-detail-grid" style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '3rem', 
        alignItems: 'start',
        background: 'white',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <div style={{ position: 'relative' }}>
          {isSoldOut && (
            <div style={{
              position: 'absolute',
              top: '20px',
              left: '20px',
              background: '#dc3545',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              fontSize: '1rem',
              fontWeight: '700',
              zIndex: 2,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Agotado
            </div>
          )}
          <div style={{ position: 'relative', width: '100%' }}>
            {/* Image Container */}
            <div className="product-image-container" style={{ 
              position: 'relative', 
              width: '100%', 
              height: '650px',
              maxHeight: '70vh',
              overflow: 'hidden',
              borderRadius: '12px',
              background: '#f8f9fa'
            }}>
              {productImages.map((img, index) => (
                <img
                  key={img.src}
                  src={proxyDetail(img.src)}
                  alt={img.alt}
                  loading="lazy"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: index === currentImageIndex ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                    filter: 'none',
                    pointerEvents: index === currentImageIndex ? 'auto' : 'none'
                  }}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/500x400/f8f9fa/333?text=Product+Image';
                  }}
                />
              ))}

              {/* Navigation Arrows - Only show if more than one image */}
              {productImages.length > 1 && (
                <>
                  <button
                    onClick={handlePreviousImage}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(255, 255, 255, 0.9)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      color: '#333',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                      zIndex: 3,
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 1)'}
                    onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.9)'}
                    aria-label={t('carousel.previous')}
                  >
                    ‹
                  </button>
                  <button
                    onClick={handleNextImage}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(255, 255, 255, 0.9)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      color: '#333',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                      zIndex: 3,
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 1)'}
                    onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.9)'}
                    aria-label={t('carousel.next')}
                  >
                    ›
                  </button>
                </>
              )}

              {/* Dot Indicators - Only show if more than one image */}
              {productImages.length > 1 && (
                <div style={{
                  position: 'absolute',
                  bottom: '15px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: '8px',
                  zIndex: 3
                }}>
                  {productImages.map((img, index) => (
                    <button
                      key={img.src}
                      onClick={() => handleDotClick(index)}
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        border: 'none',
                        background: index === currentImageIndex ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                        cursor: 'pointer',
                        padding: 0,
                        transition: 'background 0.2s ease',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
                      }}
                      aria-label={`Ver imagen ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <h1 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#333' }}>
            {displayName}
          </h1>

          {isKiosk && product.isInTransit && (
            <div style={{
              display: 'inline-block',
              background: '#007bff',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontSize: '0.95rem',
              fontWeight: '700',
              marginBottom: '1rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {t('inTransit')}
            </div>
          )}

          {product.isPreorder ? (
            <>
              <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#e4405f', marginBottom: '1.5rem' }}>
                {t('price.deposit')} {formatPrice((product.price || 0) * 0.5)}
              </p>

              <p style={{ fontSize: '1.75rem', fontWeight: '700', color: '#2ca9e1', marginBottom: '1.5rem' }}>
                {t('price.final')} {formatPrice(product.price)}
              </p>
            </>
          ) : (
            <p style={{ fontSize: '2rem', fontWeight: '700', color: '#2ca9e1', marginBottom: '1.5rem' }}>
              {t('price.label')} {formatPrice(product.price)}
            </p>
          )}

          {hasVariants && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '0.9rem', fontWeight: '600', color: '#333', marginBottom: '0.6rem' }}>
                {t('color.label')}
                {selectedVariant && (
                  <span style={{ fontWeight: '400', color: '#666' }}>
                    {' '}{(locale.startsWith('en') && selectedVariant.name_en) ? selectedVariant.name_en : selectedVariant.name}
                  </span>
                )}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                {colorVariants.map((variant) => {
                  const isSelected = variant.id === selectedVariantId;
                  const variantLabel = (locale.startsWith('en') && variant.name_en) ? variant.name_en : variant.name;
                  return (
                    <button
                      key={variant.id}
                      type="button"
                      onClick={() => handleSelectVariant(variant.id)}
                      title={variant.isSoldOut ? `${variantLabel} — ${t('color.soldOut')}` : variantLabel}
                      aria-label={variantLabel}
                      aria-pressed={isSelected}
                      style={{
                        position: 'relative',
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: variant.hex || '#ccc',
                        border: isSelected ? '2px solid #2ca9e1' : '2px solid #fff',
                        boxShadow: isSelected ? '0 0 0 1.5px #2ca9e1' : '0 0 0 1px #ddd',
                        cursor: 'pointer',
                        padding: 0,
                        opacity: variant.isSoldOut ? 0.35 : 1,
                      }}
                    >
                      {variant.isSoldOut && (
                        <span style={{
                          position: 'absolute',
                          top: '50%',
                          left: '-10%',
                          width: '120%',
                          height: '2px',
                          background: '#dc3545',
                          transform: 'translateY(-50%) rotate(-45deg)',
                        }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {displayDescription && (
            <div style={{ marginBottom: '2rem' }}>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: '#555', textAlign: 'justify', whiteSpace: 'pre-line' }}>
                {displayDescription}
              </p>
            </div>
          )}

          <div style={{ marginBottom: '2rem' }}>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {product.artist && product.artist !== 'N/A' && product.artist.trim() !== '' && (
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>{t('fields.artist')}</strong>{' '}
                  {(artist?.slug || product.artist_id) ? (
                    <Link to={`/artist/${artist?.slug || product.artist_id}`} style={{ color: '#2ca9e1' }}>
                      {artistDisplayName}
                    </Link>
                  ) : (
                    artistDisplayName
                  )}
                </li>
              )}
              {displaySeries && displaySeries !== 'N/A' && displaySeries.trim() !== '' && (
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>{t('fields.series')}</strong> {displaySeries}
                </li>
              )}
              {product.brand && product.brand !== 'N/A' && product.brand.trim() !== '' && (
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>{t('fields.brand')}</strong> {product.brand}
                </li>
              )}
              {product.size && product.size !== 'N/A' && product.size.trim() !== '' && (
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>{t('fields.size')}</strong> {product.size}
                </li>
              )}
            </ul>
          </div>


          <button 
            className="btn btn-primary"
            onClick={handleAddToCart}
            disabled={isSoldOut}
            style={{ 
              width: '100%', 
              padding: '1rem', 
              fontSize: '1.1rem',
              opacity: isSoldOut ? 0.5 : 1,
              cursor: isSoldOut ? 'not-allowed' : 'pointer'
            }}>
            {isSoldOut ? t('soldOut') : t('addToCart')}
          </button>
        </div>
      </div>

      {/* Artist Info Section — only rendered when the product is linked to
          an artist row (artist_id). Products that carry only a free-text
          `artist` string don't show a section, since there's no rich
          profile to display. */}
      {product.artist_id && (
        <div className="artist-section" style={{
          marginTop: '3rem',
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 className="artist-section-title" style={{ 
            fontSize: '1.8rem', 
            marginBottom: '2rem',
            fontFamily: "'Gambado Sans', sans-serif",
            color: '#333'
          }}>
            {t('artistSection.title')}
          </h2>
          
          {artistLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: '#666' }}>{t('artistSection.loading')}</p>
            </div>
          ) : artistError ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: '#dc3545' }}>{t('artistSection.loadError')}</p>
              {process.env.NODE_ENV === 'development' && (
                <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  {artistError}
                </p>
              )}
            </div>
          ) : artist ? (
            <>
              <div className="artist-info-grid" style={{
                display: 'grid',
                gridTemplateColumns: '200px 1fr',
                gap: '2rem',
                alignItems: 'start'
              }}>
                {/* Artist Profile Image */}
                <div className="artist-profile-image-container">
                  {artist.profile_image_url ? (
                    <div style={{
                      width: '200px',
                      height: '200px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                      display: 'block',
                      margin: '0 auto'
                    }}>
                      <BlurImage
                        src={proxyAvatar(artist.profile_image_url)}
                        alt={artistDisplayName}
                        loading="lazy"
                        objectFit="cover"
                        onError={(e) => {
                          e.currentTarget.src = 'https://via.placeholder.com/200x200/f8f9fa/333?text=Artist';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="artist-profile-placeholder" style={{
                      width: '200px',
                      height: '200px',
                      background: '#f8f9fa',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#666',
                      margin: '0 auto',
                      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                    }}>
                      {t('artistSection.noImage')}
                    </div>
                  )}
                </div>

                {/* Artist Details */}
                <div className="artist-details">
                  <div style={{ marginBottom: '1rem' }}>
                    <h3 className="artist-name" style={{
                      fontSize: '1.5rem',
                      marginBottom: '1rem',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <Link
                        to={`/artist/${artist.slug || artist.id || artist.artist_id || product.artist_id}`}
                        style={{ color: 'inherit', textDecoration: 'none', borderBottom: '2px solid transparent' }}
                        onMouseEnter={(e) => (e.currentTarget.style.borderBottomColor = '#2ca9e1')}
                        onMouseLeave={(e) => (e.currentTarget.style.borderBottomColor = 'transparent')}
                      >
                        {artistDisplayName}
                      </Link>
                    </h3>
                  </div>

                  {artistAboutText && (
                    <div className="artist-about" style={{ marginBottom: '1.5rem' }}>
                      <p style={{
                        fontSize: '1rem',
                        lineHeight: '1.6',
                        color: '#555',
                        marginBottom: '1rem',
                        textAlign: 'justify'
                      }}>
                        {artistAboutText}
                      </p>
                    </div>
                  )}

                  {/* Artist Links */}
                  <div className="artist-links" style={{ 
                    display: 'flex', 
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    {artist.instagram_handle && (
                      <a
                        href={`https://instagram.com/${artist.instagram_handle.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 1rem',
                          background: '#E4405F',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                      >
                        <svg 
                          width="18" 
                          height="18" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          style={{ flexShrink: 0 }}
                        >
                          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="white" fill="none"/>
                          <circle cx="12" cy="12" r="4" stroke="white" fill="none"/>
                          <circle cx="17.5" cy="6.5" r="1.5" fill="white"/>
                        </svg>
                        {artist.instagram_handle}
                      </a>
                    )}
                    {artist.website_url && (
                      <a
                        href={artist.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 1rem',
                          background: '#2ca9e1',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                      >
                        <span>🌐</span>
                        {t('artistSection.website')}
                      </a>
                    )}
                  </div>
              </div>
            </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ProductPage;
